import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideEye, lucideEyeOff, lucideMail, lucideTriangleAlert } from '@ng-icons/lucide';
import { HlmAlertImports } from '@spartan-ng/helm/alert';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmInputGroupImports } from '@spartan-ng/helm/input-group';
import { HlmSpinnerImports } from '@spartan-ng/helm/spinner';
import { GoogleButtonComponent } from '../../components/google-button/google-button.component';
import { LogoComponent } from '../../components/logo/logo.component';
import { PartnerService } from '../../services/partner.service';

/** 'link' asks for the mail that sets (or resets) the password; 'sent' confirms it. */
type Mode = 'login' | 'link' | 'sent';

/**
 * Login to the partner area (/partner/login). The password only ever comes from a
 * mailed link, so "no password yet" and "forgot password" are the same way in.
 */
@Component({
  selector: 'app-partner-login',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RouterModule,
    NgIcon,
    HlmAlertImports,
    HlmButtonImports,
    HlmCardImports,
    HlmFieldImports,
    HlmInputImports,
    HlmInputGroupImports,
    HlmSpinnerImports,
    LogoComponent,
    GoogleButtonComponent,
  ],
  providers: [provideIcons({ lucideEye, lucideEyeOff, lucideMail, lucideTriangleAlert })],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './partner-login.component.html',
})
export class PartnerLoginComponent {
  private readonly partnerService = inject(PartnerService);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder).nonNullable;

  readonly mode = signal<Mode>('login');
  readonly loading = signal(false);
  readonly submitted = signal(false);
  readonly error = signal<string | null>(null);
  /** The password was right, but the address isn't confirmed yet: offer a new link. */
  readonly unverifiedEmail = signal<string | null>(null);
  readonly verificationResent = signal(false);
  /** Google found no partner: point to the sign-up. */
  readonly noGooglePartner = signal(false);
  readonly sentMessage = signal('');
  readonly showPassword = signal(false);

  readonly loginForm = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
    password: ['', Validators.required],
  });
  readonly linkForm = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
  });

  constructor() {
    if (this.partnerService.isLoggedIn()) {
      this.router.navigate(['/partner/clients']);
    }
  }

  show(mode: Exclude<Mode, 'sent'>): void {
    this.error.set(null);
    this.submitted.set(false);
    // Carry the address over, so it needn't be typed twice
    if (mode === 'link') {
      this.linkForm.controls.email.setValue(this.loginForm.controls.email.value);
    }
    this.mode.set(mode);
  }

  login(): void {
    if (this.loading()) return;
    this.submitted.set(true);
    this.resetErrors();
    if (this.loginForm.invalid) return;

    const { email, password } = this.loginForm.getRawValue();
    this.loading.set(true);
    this.partnerService.login(email.trim(), password).subscribe({
      next: () => {
        this.loading.set(false);
        this.router.navigate(['/partner/clients']);
      },
      error: (err: HttpErrorResponse) => {
        this.loading.set(false);
        if (err.status === 403) {
          this.unverifiedEmail.set(email.trim().toLowerCase());
          this.verificationResent.set(false);
        }
        this.error.set(
          err.status === 401
            ? 'E-Mail-Adresse oder Passwort stimmen nicht.'
            : err.status === 403
              ? err.error?.detail
              : this.genericError(err),
        );
      },
    });
  }

  resendVerification(): void {
    const email = this.unverifiedEmail();
    if (!email) return;
    this.partnerService.resendVerification(email).subscribe({
      next: () => this.verificationResent.set(true),
    });
  }

  onGoogle(credential: string): void {
    this.resetErrors();
    this.loading.set(true);
    this.partnerService.loginWithGoogle(credential, 'login').subscribe({
      next: () => {
        this.loading.set(false);
        this.router.navigate(['/partner/clients']);
      },
      error: (err: HttpErrorResponse) => {
        this.loading.set(false);
        this.noGooglePartner.set(err.status === 404);
        this.error.set(
          err.status === 404 || err.status === 403
            ? err.error?.detail
            : 'Die Anmeldung mit Google ist fehlgeschlagen. Bitte versuche es erneut.',
        );
      },
    });
  }

  private resetErrors(): void {
    this.error.set(null);
    this.unverifiedEmail.set(null);
    this.noGooglePartner.set(false);
  }

  requestLink(): void {
    if (this.loading()) return;
    this.submitted.set(true);
    this.error.set(null);
    if (this.linkForm.invalid) return;

    this.loading.set(true);
    this.partnerService.requestPasswordLink(this.linkForm.getRawValue().email.trim()).subscribe({
      next: (response) => {
        this.loading.set(false);
        this.sentMessage.set(response.message);
        this.mode.set('sent');
      },
      error: (err: HttpErrorResponse) => {
        this.loading.set(false);
        this.error.set(this.genericError(err));
      },
    });
  }

  invalid(form: 'login' | 'link', field: 'email' | 'password'): boolean {
    const control =
      form === 'login' ? this.loginForm.controls[field] : this.linkForm.controls[field as 'email'];
    return (this.submitted() || control.touched) && control.invalid;
  }

  private genericError(err: HttpErrorResponse): string {
    return err.status === 429
      ? 'Zu viele Versuche. Bitte warte eine Minute und versuche es erneut.'
      : 'Ein Fehler ist aufgetreten. Bitte versuche es später erneut.';
  }
}
